<template>
  <main class="campaign-creator">
    <div class="editor-container">
      <header v-if="!classicCampaignsEnabled">
        <div class="display-name">
          {{ currentMerchant.display_name }}
        </div>
        <div class="header-images">
          <template v-for="(stashboardHeader, index) in stashboardHeadersList">
            <img v-if="stashboardHeader.attachment"
                 :key="index"
                 :src="cleanIconUrl(stashboardHeader.attachment.url)"
                 class="stashboard-icon-preview"
                 title="stashboard image"/>
          </template>
        </div>
      </header>

      <add-image-modal
        v-if='showImageModal'
        class="image-modal"
        :selected.sync="selectedModalImageSrc"
        :images="images"
        :loading.sync="modalLoading"
        @resolved="didResolveImageModal"
        @rejected="didRejectImageModal"
        @removeUpload="shouldRemoveSelectedImage"
        @shouldUpload="shouldUploadImage"
      />

      <section class="image-container" v-if="!classicCampaignsEnabled">
        <sb-loading-icon v-if="loading"/>

        <template v-else>
          <template v-if="!attachedImageSrc">
            <button class="rounded-blue" @click="clickedAddImageBtn" data-cy="add-image-button">
              <fai :icon="['far', 'image']" size="2x" />
              add image
            </button>
          </template>

          <template v-if="attachedImageSrc">
            <img :src="attachedImageSrc" />
            <button class='remove-btn' @click="clickedRemove" data-cy="remove-image-button">remove</button>
          </template>
        </template>
      </section>

      <editor
        :value="editorValue"
        @update:value="updateValue"
        ref="editor"
        class='border-can-error'
        v-touch:sms_content
        v-has-error:sms_content
      />

      <campaign-action-bar
        class="border-can-error"
        :campaign="campaign"
        @shouldInsertText="shouldInsertText"
      />
    </div>
  </main>
</template>

<script>
import Editor from "./editor";
import CampaignActionBar from "./action-bar/campaign-action-bar";
import {mapGetters} from "vuex";
import {attachmentAPI} from "../../services/api/attachmentAPI";
import {AddImageModal, Touch, HasError, UserError} from "@springbig/sb-components";

export default {
  name: "campaign-creator",
  directives: {Touch, HasError},
  components: {CampaignActionBar, Editor, AddImageModal},

  props: {
    campaign: Object,
    ad: Object,
    editorValue: String,
    stashboardHeaders: Array
  },

  data() {
    return {
      showImageModal: false,
      storedImages: [],
      selectedModalImageSrc: null,
      attachedImage: null,
      uploadedImage: null,
      loading: false,
      modalLoading: false,
      maxAttachmentSize: 1024 * 1024 * 10, // 10 mb
    };
  },

  async created() {
    if(this.ad) {
      this.attachedImage = this.ad.attachments[0];
    } else {
      this.loading = true;
      const attachments = await attachmentAPI.forCampaign(this.campaign.id, { label: ['mms'] });
      this.attachedImage = attachments[0];
      await this.refreshStoredImages();
      this.loading = false;

      this.$emit('attachedImage', this.attachedImage);
    }
  },

  methods: {
    validateFile(file) {
      if(file.size >= this.maxAttachmentSize) {
        return "issue uploading image: file size must be less than 10 mb";
      }
    },
    shouldInsertText(text) {
      this.$refs.editor.insertText(text);
    },
    async updateValue(text) {
      this.$emit("update:editor-value", text);
    },
    async refreshStoredImages() {
      this.loading = true;
      const resp = await attachmentAPI.storedMMS('Campaign');
      this.storedImages = resp.stored_mms;
      this.loading = false;
    },
    clickedAddImageBtn() {
      this.showImageModal = true;
    },
    async didResolveImageModal() {
      this.showImageModal = false;
      this.loading = true;
      const image = this.storedImages.find((image) => image.url === this.selectedModalImageSrc);

      /**
       * We tell the API to attach stored images. Uploaded images are automatically attached when uploaded
       */
      if(!this.uploadedImage || image.id !== this.uploadedImage.id) {
        this.attachedImage = await attachmentAPI.attachStoredImageToCampaign(this.campaign.id, image.id);
      } else {
        this.attachedImage = image;
      }

      this.uploadedImage = null;
      this.loading = false;

      this.$emit('attachedImage', this.attachedImage);
    },
    async didRejectImageModal() {
      if(this.uploadedImage && this.attachedImage === this.uploadedImage) {
        await attachmentAPI.delete(this.uploadedImage.uuid);
        this.attachedImage = null;
      }

      this.selectedModalImageSrc = this.attachedImageSrc;
      this.showImageModal = false;
      this.uploadedImage = null;
    },
    async deleteAttachedImage() {
      this.loading = true;
      if(this.ad) {
        this.$emit('update:ad', null, this.ad);
      } else {
        await attachmentAPI.delete(this.attachedImage.uuid)
        await this.refreshStoredImages();
      }
      this.attachedImage = null;
      this.selectedModalImageSrc = null;
      this.loading = false;
    },
    async clickedRemove() {
      await this.deleteAttachedImage()
      this.$emit('attachedImage', this.attachedImage);
    },
    async shouldRemoveSelectedImage() {
      if(this.attachedImage && this.uploadedImage === this.attachedImage) {
        const resp = await attachmentAPI.delete(this.attachedImage.uuid)
        await this.refreshStoredImages();
      }

      this.uploadedImage = null;
      this.attachedImage = null;
      this.selectedModalImageSrc = null;
      this.modalLoading = false;
    },

    async shouldUploadImage(file) {
      this.$store.dispatch('DISMISS_ERRORS', { modal: 'add image' });

      const error = this.validateFile(file);
      if(error) {
        this.$store.dispatch('ADD_ERRORS', [ new UserError(new Error(), "issue uploading image: file size must be less than 10 mb") ]);
      } else {
        const resp = await attachmentAPI.create(this.campaign.id, 'mms', 'Campaign', file);
        this.attachedImage = resp;
        this.uploadedImage = resp;
        this.selectedModalImageSrc = this.attachedImageSrc;
        this.storedImages.unshift(resp);
      }


      this.modalLoading = false;
    },

    cleanIconUrl(url) {
      if (url.startsWith("http")) {
        return `${url}?h=47&w=47`;
      }
      return url;
    }
  },

  watch: {
    async ad(newAd, oldAd) {
      if(oldAd && oldAd.attachments[0] === this.attachedImage) {
        this.attachedImage = null;
      }

      //Need to delete previous attachment before updating to new ad
      if(this.attachedImage) {
        await this.deleteAttachedImage();
      }

      if(newAd) {
        this.attachedImage = newAd.attachments[0];
      }
    }
  },

  computed: {
    invalidText() { return this.$store.getters.hasError('sms_content'); },
    ...mapGetters(['classicCampaignsEnabled', 'currentMerchant']),

    images() {
      return this.storedImages.map((stored) => stored.url).slice(0,21);
    },
    attachedImageSrc() {
      return this.attachedImage ? this.attachedImage.url : null;
    },
    stashboardHeadersList() {
      return this.stashboardHeaders;
    }
  }
}
</script>

<style scoped>
.campaign-creator {
  display:flex;
  flex-direction: column;
  flex-wrap: nowrap;
}

.editor-container {
  display:flex;
  flex-direction: column;
  width: 100%;
}

.invalid-text {
  border-color: #f91253;
}

.sb-loading-icon >>> svg {
  fill: #128ff9;
}

.image-container {
  position: relative;
  width: 100%;
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #A6D4FC;
  border-left: 1px solid #A6D4FC;
}

.image-container button {
  font-family: "AileronThin";
  font-size: 18px;
}

.image-modal >>> header > div {
  position: relative;
}

.image-modal >>> div {
  overflow-y: auto;
}

.image-modal >>> footer {
  margin: 0;
}

.image-modal >>> footer > button {
  position: absolute;
  top: 12px;
  right: 10px;
  line-height: 10px;
  border-radius: 12px;
}

.image-modal >>> .add-image-modal {
  height: auto;
  min-width: 600px;
}

header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 10px;
  background-color: #A6D4FC;
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
}

header .logo {
  height: 20px;
  width: auto;
}

.rounded-blue {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.remove-btn {
  cursor: pointer;
  position: absolute;
  bottom: 10px;
  background: white;
  padding: 4px 8px;
  border-radius: 4px;
  color: #A6D4FC;
  border-color: #A6D4FC;
}

img {
  height: 100%;
}

.display-name {
  width: 50%;
}

.header-images {
  width: 50%;
  text-align: right;
}

.stashboard-icon-preview {
  width: 25px;
  height: 25px;
  margin: 0 3px;
}

@media only screen and (max-width: 1023px) {
  .image-modal >>> .add-image-modal {
    min-width: 350px;
  }

  .image-modal >>> footer > button {
    position: relative;
    top: unset;
    right: unset;
    line-height: unset;
  }
}

</style>
